"use strict";

var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.hasOnlyTeamProducts = exports.hasTeamProducts = exports.hasEnterpriseProducts = exports.hasSomeFulfillableItems = exports.hasAllFulfillableItems = exports.hasSupportRoleAssignmentAllowed = exports.hasSupportCaseCreationAllowed = exports.hasProductSupportRoleAssignmentAllowed = exports.hasProductsInCloud = exports.getSupportFulfillableItems = exports.getSupportedProducts = exports.getProductById = exports.findFulfillableItem = void 0;

const flatMap_1 = __importDefault(require("lodash/flatMap"));

const flow_1 = __importDefault(require("lodash/flow"));

const uniqBy_1 = __importDefault(require("lodash/uniqBy"));

const types_1 = require("./types");
/**
 * @description Get a fulfillable item from a product list by code
 *
 * @param {ProductList} productList - the product list.
 * @param {String} code - the fi code to find.
 * @returns {FulfillableItem} the found fulfillable item or undefined if none found
 */


const findFulfillableItem = (productList, code) => {
  return flow_1.default(products => flatMap_1.default(products, 'fulfillableItems.items'), fulfillableItems => {
    return fulfillableItems.find(fi => fi && fi.code === code);
  })(productList.items);
};

exports.findFulfillableItem = findFulfillableItem;
/**
 * @description Returns product that matches the ID.
 *
 * @param {ProductList} productList - the product list.
 * @param {id} id - product ID
 * @returns {Product} the product that matches the ID in the product list.
 */

const getProductById = (productList, id) => {
  return productList.items.find(product => product.id === id);
};

exports.getProductById = getProductById;
/**
 * @description Returns product instance that has supported_product FI.
 *
 * @param {ProductList} productList - the product list.
 * @param {String[]} productSupportRoleProductIds - product ids that require product support admin role
 * @param {Boolean} forExpertSession - boolean parameter to return product based on whether it is for
 * Cases or ExpertSession.
 * @param {Boolean} userIsOrgOrSupportAdmin - whether the user is an org or support admin
 * @returns {Product[]} the product instances contain supported_product FI
 */

const getSupportedProducts = (productList, productSupportRoleProductIds, forExpertSession, userIsOrgOrSupportAdmin) => {
  return getFilteredProducts(productList, {}, productSupportRoleProductIds, forExpertSession, true, userIsOrgOrSupportAdmin);
};

exports.getSupportedProducts = getSupportedProducts;
/**
 * @description Returns the fulfillable items for which support tickets can be created.
 *
 *  Support tickets can be created for a product's fulfillable items only if it has the
 * support_case_creation_allowed FI.
 *
 *  For such a product, tickets can be created for its "SUPPORT" type FIs. If the product has
 * no SUPPORT FIs, tickets can be created for its DESKTOP, PRODUCT or QUOTA FIs.
 *
 * The set of products whose FIs are returned by this method can be further filtered via the
 * optional parameter.
 *
 * @param {ProductList} productList - the product list.
 * @param {Object} productFilter - options passed to _.filter for limiting which products'
 *   FIs are returned, e.g. {cloud: 'CREATIVE'} to return only FIs of Creative Cloud products.
 * @param {String[]} productSupportRoleProductIds - product ids that require product support admin role
 * @param {Boolean} forExpertSession - boolean parameter to return FI based on whether it is for
 * Cases or ExpertSession.
 * @param {Boolean} userIsOrgOrSupportAdmin - whether the user is an org or support admin
 * @returns {FulfillableItem[]} the fulfillable items for which tickets can be created
 */

const getSupportFulfillableItems = (productList, productFilter, productSupportRoleProductIds, forExpertSession, userIsOrgOrSupportAdmin) => {
  const filteredProducts = getFilteredProducts(productList, productFilter, productSupportRoleProductIds, forExpertSession, false, userIsOrgOrSupportAdmin);
  const unflattenFIs = filteredProducts.map(product => {
    const fiTypesToReturn = product.fulfillableItems.hasSupportFulfillableItemType() ? [types_1.FULFILLABLE_ITEM_TYPE.SUPPORT] : [types_1.FULFILLABLE_ITEM_TYPE.DESKTOP, types_1.FULFILLABLE_ITEM_TYPE.QUOTA, types_1.FULFILLABLE_ITEM_TYPE.SERVICE];
    return product.fulfillableItems.items.filter(item => // exclude supported_product FI because we need product info to display it in dropdown
    item.code !== types_1.FULFILLABLE_ITEM_CONSTANTS.FULFILLABLE_ITEM_CODE.SUPPORTED_PRODUCT && fiTypesToReturn.includes(item.fulfillableItemType));
  });
  return uniqBy_1.default(flatMap_1.default(unflattenFIs), 'code');
};

exports.getSupportFulfillableItems = getSupportFulfillableItems;
/**
 * @description Returns true if this list contains any products with the specified cloud property
 *
 * @param {ProductList} productList - the product list.
 * @param {CLOUD} cloud - the CLOUD to check for (e.g. CLOUD.CREATIVE)
 * @returns {Boolean} true if this list contains
 */

const hasProductsInCloud = (productList, cloud) => {
  return productList.items.some(product => product.cloud === cloud);
};

exports.hasProductsInCloud = hasProductsInCloud;
/**
 * @description Method to determine if any products in the list are
 *              eligible for product-specific support.
 *
 * @param {ProductList} productList - the product list.
 * @returns {Boolean} true if products entitled to product-specific support exist, else false
 */

const hasProductSupportRoleAssignmentAllowed = productList => {
  return productList.items.some(product => product.isProductSupportRoleAssignmentAllowed());
};

exports.hasProductSupportRoleAssignmentAllowed = hasProductSupportRoleAssignmentAllowed;
/**
 * @description Method to determine if any products in the list are
 *              eligible for Enterprise-level support including support case creation.
 *
 * @param {ProductList} productList - the product list.
 * @returns {Boolean} true if products entitled to Enterprise-level support exist, else false
 */

const hasSupportCaseCreationAllowed = productList => {
  return productList.items.some(product => product.fulfillableItems.hasSupportCaseCreationAllowed());
};

exports.hasSupportCaseCreationAllowed = hasSupportCaseCreationAllowed;
/**
 * @description Method to determine if any products in the list are
 *              eligible for Enterprise-level support.
 *
 * @param {ProductList} productList - the product list.
 * @returns {Boolean} true if products entitled to Enterprise-level support exist, else false
 */

const hasSupportRoleAssignmentAllowed = productList => {
  return productList.items.some(product => product.fulfillableItems.hasSupportRoleAssignmentAllowed());
};

exports.hasSupportRoleAssignmentAllowed = hasSupportRoleAssignmentAllowed;
/**
 * @description Method to determine if product list contains all
 *              passed fulfillableItems
 * @param {JilModelList<Product>} productList
 * @param {string[]} fulfillableItems An array of fulfillable item codes to be checked
 * @returns {boolean}
 */

const hasAllFulfillableItems = (productList, fulfillableItems) => {
  var _a;

  return (_a = fulfillableItems === null || fulfillableItems === void 0 ? void 0 : fulfillableItems.every(fulfillableItem => exports.findFulfillableItem(productList, fulfillableItem))) !== null && _a !== void 0 ? _a : true;
};

exports.hasAllFulfillableItems = hasAllFulfillableItems;
/**
 * @description Method to determine if product list contains at least
 *              one passed fulfillableItems
 * @param {JilModelList<Product>} productList
 * @param {string[]} fulfillableItems An array of fulfillable item codes to be checked
 * @returns {boolean}
 */

const hasSomeFulfillableItems = (productList, fulfillableItems) => {
  var _a;

  return (_a = fulfillableItems === null || fulfillableItems === void 0 ? void 0 : fulfillableItems.some(fulfillableItem => exports.findFulfillableItem(productList, fulfillableItem))) !== null && _a !== void 0 ? _a : true;
};

exports.hasSomeFulfillableItems = hasSomeFulfillableItems;

const fulfillableItemsCheck = (forExpertSession, forSupportedProduct, product) => {
  return (forExpertSession ? product.fulfillableItems.hasExpertSessionCreationAllowed() : product.fulfillableItems.hasSupportCaseCreationAllowed()) && (forSupportedProduct ? product.fulfillableItems.hasSupportedProduct() : !product.fulfillableItems.hasSupportedProduct());
};

const getFilteredProducts = (productList, productFilter, productSupportRoleProductIds, forExpertSession, forSupportedProduct, userIsOrgOrSupportAdmin) => {
  return productList.items.filter(product => fulfillableItemsCheck(forExpertSession, forSupportedProduct, product) && Object.keys(productFilter).every(key => productFilter[key] === product[key]) && ( // only allow non-PSA product if user is an org or support admin
  userIsOrgOrSupportAdmin && !product.isProductSupportRoleAssignmentAllowed() || product.isProductSupportRoleAssignmentAllowed() && (productSupportRoleProductIds === null || productSupportRoleProductIds === void 0 ? void 0 : productSupportRoleProductIds.includes(product.id))));
};
/**
 * @description Method to determine if any products in the list are enterprise.
 *
 * @param {ProductList} productList - the product list.
 * @returns {Boolean} true if any products are enterprise, else false
 */


const hasEnterpriseProducts = productList => {
  return productList.items.some(product => product.isEnterprise());
};

exports.hasEnterpriseProducts = hasEnterpriseProducts;
/**
 * @description Method to determine if products contain at least one team product
 *
 * @param {ProductList} productList - the product list.
 * @returns {Boolean} true if there is at least one team product, else false
 */

const hasTeamProducts = productList => {
  return productList.items.some(product => product.isTeam());
};

exports.hasTeamProducts = hasTeamProducts;
/**
 * @description Method to determine if all of the products are team products
 *
 * @param {ProductList} productList - the product list.
 * @returns {Boolean} true if there is at least one product and they are all team products, else false
 */

const hasOnlyTeamProducts = productList => {
  return productList.items.length > 0 && productList.items.every(product => product.isTeam());
};

exports.hasOnlyTeamProducts = hasOnlyTeamProducts;